import { ref, watch, onUnmounted } from 'vue'
import { useConversationStore } from '@/stores/conversationStore'

/*
  ポーリングの仕組み
  1. 初回は1秒後にfetch_active_txを実行
  2. activeTxがある場合は1秒間隔、ない場合は1分間隔でポーリング
  3. 10分経過後にポーリングを自動停止
*/
export function useConversationPolling() {
  const conversationStore = useConversationStore()
  const timeoutId = ref<number | null>(null)
  const pollingStartTime = ref<number | null>(null)
  const currentConversationId = ref<number | null>(null)

  function startPolling(conversationId: number, isInitial = true) {
    // 初回の場合は既存のポーリングを破棄して新規開始
    if (isInitial) {
      stopPolling()
      pollingStartTime.value = Date.now()
      currentConversationId.value = conversationId
    }

    const now = Date.now()
    const elapsed = now - (pollingStartTime.value || now)

    // Stop polling after 10 minutes
    if (elapsed >= 10 * 60 * 1000) {
      return
    }

    // Determine interval based on activeTx
    // 初回は必ず1秒後に次のポーリングを実行して最新状態を確認
    const interval = isInitial ? 1000 : conversationStore.activeTx ? 1000 : 60000

    timeoutId.value = window.setTimeout(() => {
      // Fetch transaction status
      const conversation = conversationStore.currentConversation
      if (conversation && conversation.id === conversationId) {
        const lastMessageId = conversation.chatMessages[conversation.chatMessages.length - 1]?.id
        conversationStore.fetch_active_tx(conversationId, lastMessageId)
      }

      // Continue polling
      startPolling(conversationId, false)
    }, interval)
  }

  function stopPolling() {
    if (timeoutId.value) {
      window.clearTimeout(timeoutId.value)
      timeoutId.value = null
    }
    pollingStartTime.value = null
    currentConversationId.value = null
  }

  // Watch for active transaction changes
  watch(
    () => conversationStore.activeTx,
    (newVal, oldVal) => {
      if (newVal === null && oldVal === null) {
        return
      }

      if (newVal !== null && oldVal === null) {
        // New transaction started - reset polling start time
        pollingStartTime.value = null
      }

      if (newVal === null && oldVal !== null) {
        // Transaction completed
        pollingStartTime.value = null

        // Reload all messages when transaction completes
        if (currentConversationId.value) {
          conversationStore.get_one_history(currentConversationId.value)
        }
      }
    },
  )

  // Cleanup on unmount
  onUnmounted(() => {
    stopPolling()
  })

  return {
    startPolling,
    stopPolling,
  }
}
